import { NgIf } from '@angular/common';
import { Component, Input, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FormDataService } from '../../../Services/Form Data/form-data.service';
import { DataService } from '../../../Services/Data Service/data.service';
import { AuthService } from '../../../Auth/auth.service';
import { firstValueFrom, forkJoin, switchMap, take } from 'rxjs';
import { FormUpdateService } from '../../../Services/Form Data/formupdate.service';
import { DisableAutocompleteDirective } from '../../../disable-autocomplete.directive';

@Component({
  selector: 'app-c-documents-form',
  standalone: true,
  imports: [FormsModule, NgIf, DisableAutocompleteDirective],
  templateUrl: './c-documents-form.component.html',
  styleUrl: './c-documents-form.component.css'
})
export class CDocumentsFormComponent {
  @Input() isViewOnly: boolean = false;
  @Input() formData: any; 
  
  documents: any = '';

  loading = false;
  currUser: any;
  savedMessage = '';
  minDate: string;


  constructor(
    private formDataService: FormDataService, 
    private dataService: DataService, 
    private formUpdateService: FormUpdateService,
    private auth: AuthService) {
    this.currUser = this.formDataService.getUserData(); 
    this.documents = this.formDataService.getDocumentsData(); // Retrieve existing document data
    
    // Set minimum date to today using dataService
    const today = new Date(this.dataService.getLocalDate());
    this.minDate = today.toISOString().split('T')[0];
  }

  error_message = '';

  resetErrorMessage() {
    this.error_message = "";
  }
  
  ngOnChanges(changes: SimpleChanges) {
    if (changes['formData']){
      if (this.formData?.documents) {
        this.documents = this.formData.documents;
      }
    }
  }

  onFileSelected(event: Event, fileType: string) {
    const input = event.target as HTMLInputElement;
    
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
  
      // PDF LIMIT
      if (file.type !== 'application/pdf') {
        alert('Only PDF files are allowed.');
        input.value = ''; // Clear the file input
        return;
      }
  
      // FILE SIZE LIMIT
      const fileSizeLimit = 10 * 1024 * 1024; // 10 MB 
      if (file.size > fileSizeLimit) {
        alert('The file is too large. Please select a file smaller than 20 MB.');
        input.value = ''; // Clear the file input
        return;
      }

      switch (fileType) {
        case 'vatFile':
          this.documents.vatFile = file;
          break;
        case 'birFile':
          this.documents.birFile = file;
          break;
        case 'nibFile':
          this.documents.nibFile = file;
          break;
        case 'incorpFile':
          this.documents.incorpFile = file;
          break;
        case 'regFile':
          this.documents.regFile = file;
          break;
        case 'compFile':
          this.documents.companyFile = file;
          break;
        case 'licFile':
          this.documents.licenseFile = file;
          break;
      }
    }
  }

  validatefields(): boolean {
    console.log("Documents: ", this.documents)

    if (
     !this.documents.hasVatDocument?.trim()
     || !this.documents.hasBirDocument?.trim()
     || !this.documents.hasCompanyProfile?.trim()
     || !this.documents.hasGeneralLicenses?.trim()
     || !this.documents.hasNibDocument?.trim()
     || !this.documents.hasRegistrationCertificate?.trim()
     || !this.documents.hasIncorporationCertificate?.trim()
    ) 
    {
      this.error_message = "Please respond to all sections."; 
      return false;
    }

      // VAT Validators
      if (this.documents.hasVatDocument === 'yes') {
        if (!this.documents.vatRegistrationNo) {
          this.error_message = "Please enter VAT Registration No.";
          return false;
        }
        if (!this.documents.vatExpiryDate) {
          this.error_message = "Please enter VAT expiry date.";
          return false;
        }
        // Check if VAT expiry date is in the past
        const vatExpiryDate = new Date(this.documents.vatExpiryDate);
        const today = new Date(this.dataService.getLocalDate());
        today.setHours(0, 0, 0, 0); // Reset time to start of day
        if (vatExpiryDate < today) {
          this.error_message = "VAT expiry date cannot be in the past.";
          return false;
        }
        if (!this.documents.vatFile && !this.documents.vatFilePath) {
          this.error_message = "Please upload VAT document.";
          return false;
        }
      } else if (this.documents.hasVatDocument === 'no' && !this.documents.noVatReason.trim()) {
        console.log("noVatReason value:", this.documents.noVatReason);

        this.error_message = "Please enter reason for no VAT.";
        return false;
      }
    
      // BIR Validators
      if (this.documents.hasBirDocument === 'yes') {
        if (!this.documents.birNo) {
          this.error_message = "Please enter BIR No.";
          return false;
        }
        if (!this.documents.birFile && !this.documents.birFilePath ) {
          this.error_message = "Please upload BIR document.";
          return false;
        }
        // if (!this.documents.birExpiryDate) {
        //   this.error_message = "Please enter BIR expiry date.";
        //   return false;
        // }
      } else if (this.documents.hasBirDocument === 'no' && !this.documents.noBirReason) {
        this.error_message = "Please enter reason for no BIR.";
        return false;
      }

      // NIB Validators
      if (this.documents.hasNibDocument === 'yes') {
        if (!this.documents.nibNo) {
          this.error_message = "Please enter NIB No.";
          return false;
        }
        if (!this.documents.nibExpiryDate) {
          this.error_message = "Please enter NIB expiry date.";
          return false;
        }
        // Check if NIB expiry date is in the past
        const nibExpiryDate = new Date(this.documents.nibExpiryDate);
        const today = new Date(this.dataService.getLocalDate());
        today.setHours(0, 0, 0, 0); // Reset time to start of day
        if (nibExpiryDate < today) {
          this.error_message = "NIB expiry date cannot be in the past.";
          return false;
        }
        if (!this.documents.nibFile && !this.documents.nibFilePath) {
          this.error_message = "Please upload NIB document.";
          return false;
        }
      } else if (this.documents.hasNibDocument === 'no' && !this.documents.noNibReason) {
        this.error_message = "Please enter reason for no NIB.";
        return false;
      }

      // Incorporation Cert. Validators
      if (this.documents.hasIncorporationCertificate === 'yes') {
        if (!this.documents.incorpFile && !this.documents.incorporationFilePath) {
          this.error_message = "Please upload Incorporation Certificate.";
          return false;
        }
      } else if (this.documents.hasIncorporationCertificate === 'no' && !this.documents.noIncorporationReason) {
        this.error_message = "Please enter reason for no Incorporation Certificate.";
        return false;
      }

      // Registration Cert. Validators
      if (this.documents.hasRegistrationCertificate === 'yes') {
        if (!this.documents.regFile && !this.documents.registrationFilePath) {
          this.error_message = "Please upload Registration Certificate.";
          return false;
        }
      } 

      // Company Profile Validators
      if (this.documents.hasCompanyProfile === 'yes') {
        if (!this.documents.companyFile && !this.documents.companyProfilePath){
          this.error_message = "Please upload Company Profile.";
          return false;
        }
      } 

      // Licenses Validators
      if (this.documents.hasGeneralLicenses === 'yes') {
        if (!this.documents.licenseFile && !this.documents.generalLicensesPath){
          this.error_message = "Please upload General Licenses.";
          return false;
        }
      } 

    this.error_message = "";
    return true;
  }

  async onSubmit(): Promise<boolean>{
    if (!this.validatefields()) {
     return false; // Validation failed
    }
  
    if (this.error_message !== "") {
      return false; // Error message present, do not proceed
    }

    this.loading = true;
    this.error_message = "";

    //Update Locally
    this.formDataService.updateDocumentsData(this.documents);

    //SAVE documents TO DB
    try {
      await firstValueFrom(
        this.formUpdateService.updateDocuments(this.formDataService.getCurrCompanyID())
      );
       this.loading = false;
      return true;
    } catch (error) {
      this.error_message = "There was an issue saving. Please try again.";
      this.loading = false;
      return false;
    }
  }

}
